"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useMemo } from "react";
import UserCICAndLevel, { UserCICAndLevelSize } from "@/components/user/utils/UserCICAndLevel";
import Link from "next/link";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import type { ApiDropPart } from "@/generated/models/ApiDropPart";
import DropPartMarkdownWithPropLogger from "@/components/drops/view/part/DropPartMarkdownWithPropLogger";
import WaveDropTime from "./time/WaveDropTime";
import { getWaveRoute } from "@/helpers/navigation.helpers";
import Image from "next/image";
import { resolveIpfsUrlSync } from "@/components/ipfs/IPFSContext";
interface WaveDropQuoteProps {
  readonly drop: ApiDrop | null;
  readonly partId: number;
  readonly onQuoteClick: (drop: ApiDrop) => void;
  readonly embedPath?: readonly string[] | undefined;
  readonly quotePath?: readonly string[] | undefined;
  readonly embedDepth?: number | undefined;
  readonly maxEmbedDepth?: number | undefined;
}
const WaveDropQuote: React.FC<WaveDropQuoteProps> = t0 => {
  const $ = _c(66);
  const {
    drop,
    partId,
    onQuoteClick,
    embedPath,
    quotePath,
    embedDepth,
    maxEmbedDepth
  } = t0;
  let t1;
  bb0: {
    if (!drop) {
      t1 = null;
      break bb0;
    }
    let t2;
    if ($[0] !== drop.parts || $[1] !== partId) {
      t2 = drop.parts.find(part => part.part_id === partId) ?? null;
      $[0] = drop.parts;
      $[1] = partId;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    t1 = t2;
  }
  const quotedPart = t1;
  let t2;
  if ($[3] !== drop) {
    t2 = () => {
      if (!drop) {
        return <div className="tw-h-full tw-w-full tw-max-w-full tw-animate-pulse tw-overflow-hidden tw-rounded-md tw-bg-iron-900" />;
      }
      const resolvedPfp = drop.author.pfp ? resolveIpfsUrlSync(drop.author.pfp) : null;
      if (resolvedPfp) {
        return <div className="tw-relative tw-h-full tw-w-full"><Image src={resolvedPfp} alt={`${drop.author.handle ?? drop.author.primary_address}'s profile picture`} fill={true} sizes="24px" className="tw-rounded-md tw-bg-transparent tw-object-cover" /></div>;
      }
      return <div className="tw-h-full tw-w-full tw-max-w-full tw-overflow-hidden tw-rounded-md tw-bg-iron-900" />;
    };
    $[3] = drop;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const renderProfilePicture = t2;
  let t3;
  if ($[5] !== drop || $[6] !== onQuoteClick) {
    t3 = () => {
      if (drop?.wave.id && drop.id) {
        onQuoteClick(drop);
      }
    };
    $[5] = drop;
    $[6] = onQuoteClick;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const goToQuoteDrop = t3;
  let t4;
  bb1: {
    if (!drop) {
      t4 = "";
      break bb1;
    }
    const waveDetails = drop.wave as unknown as {
      chat?: {
        scope?: {
          group?: {
            is_direct_message?: boolean | undefined;
          } | undefined;
        } | undefined;
      } | undefined;
    };
    const isDirectMessage = waveDetails.chat?.scope?.group?.is_direct_message ?? false;
    let t5;
    if ($[8] !== drop.wave.id || $[9] !== isDirectMessage) {
      t5 = getWaveRoute({
        waveId: drop.wave.id,
        isDirectMessage,
        isApp: false
      });
      $[8] = drop.wave.id;
      $[9] = isDirectMessage;
      $[10] = t5;
    } else {
      t5 = $[10];
    }
    t4 = t5;
  }
  const waveHref = t4;
  let path;
  if ($[11] !== drop || $[12] !== quotePath) {
    path = quotePath ? [...quotePath] : [];
    if (drop?.wave.id) {
      const currentQuoteKey = `${drop.wave.id}:${drop.serial_no}`;
      if (!path.includes(currentQuoteKey)) {
        path.push(currentQuoteKey);
      }
    }
    $[11] = drop;
    $[12] = quotePath;
    $[13] = path;
  } else {
    path = $[13];
  }
  const effectiveQuotePath = path;
  let t5;
  let t6;
  if ($[14] !== goToQuoteDrop) {
    t5 = e => {
      e.stopPropagation();
      goToQuoteDrop();
    };
    t6 = e_0 => {
      if (e_0.key === "Enter" || e_0.key === " ") {
        e_0.preventDefault();
        e_0.stopPropagation();
        goToQuoteDrop();
      }
    };
    $[14] = goToQuoteDrop;
    $[15] = t5;
    $[16] = t6;
  } else {
    t5 = $[15];
    t6 = $[16];
  }
  let t7;
  if ($[17] !== renderProfilePicture) {
    t7 = <div className="tw-relative tw-h-6 tw-w-6 tw-flex-shrink-0 tw-rounded-md tw-bg-iron-900"><div className="tw-h-full tw-w-full tw-rounded-md">{renderProfilePicture()}</div></div>;
    $[17] = renderProfilePicture;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== drop) {
    t8 = !!drop && <UserCICAndLevel level={drop.author.level} size={UserCICAndLevelSize.SMALL} />;
    $[19] = drop;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const t9 = `/${drop?.author.handle ?? drop?.author.primary_address}`;
  const t10 = drop?.author.handle ?? drop?.author.primary_address;
  let t11;
  if ($[21] !== t10 || $[22] !== t9) {
    t11 = <p className="tw-mb-0 tw-text-md tw-font-semibold tw-leading-none"><Link href={t9} className="tw-text-iron-200 tw-no-underline tw-transition tw-duration-300 tw-ease-out hover:tw-text-iron-500">{t10}</Link></p>;
    $[21] = t10;
    $[22] = t9;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t11 || $[25] !== t8) {
    t12 = <div className="tw-flex tw-items-center tw-gap-x-2">{t8}{t11}</div>;
    $[24] = t11;
    $[25] = t8;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== drop) {
    t13 = !!drop && <><div className="tw-size-[3px] tw-flex-shrink-0 tw-rounded-full tw-bg-iron-600" /><WaveDropTime timestamp={drop.created_at} /></>;
    $[27] = drop;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== t12 || $[30] !== t13) {
    t14 = <div className="tw-flex tw-items-center tw-gap-x-2">{t12}{t13}</div>;
    $[29] = t12;
    $[30] = t13;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== drop || $[33] !== waveHref) {
    t15 = drop && waveHref && <Link href={waveHref} className="tw-leading-0 -tw-mt-1 tw-text-[11px] tw-text-iron-500 tw-no-underline tw-transition tw-duration-300 tw-ease-out hover:tw-text-iron-300">{drop.wave.name}</Link>;
    $[32] = drop;
    $[33] = waveHref;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== t15) {
    t16 = <div>{t15}</div>;
    $[35] = t15;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  const t17 = quotedPart?.content ?? "";
  let t18;
  if ($[37] !== drop?.mentioned_users) {
    t18 = drop?.mentioned_users ?? [];
    $[37] = drop?.mentioned_users;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] !== drop?.mentioned_waves) {
    t19 = drop?.mentioned_waves ?? [];
    $[39] = drop?.mentioned_waves;
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  let t20;
  if ($[41] !== drop?.referenced_nfts) {
    t20 = drop?.referenced_nfts ?? [];
    $[41] = drop?.referenced_nfts;
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  const t21 = drop?.nft_links;
  const t22 = drop?.id;
  let t23;
  if ($[43] !== effectiveQuotePath || $[44] !== embedDepth || $[45] !== embedPath || $[46] !== maxEmbedDepth || $[47] !== onQuoteClick || $[48] !== t17 || $[49] !== t18 || $[50] !== t19 || $[51] !== t20 || $[52] !== t21 || $[53] !== t22) {
    t23 = <div className="tw-mt-0.5"><DropPartMarkdownWithPropLogger partContent={t17} mentionedUsers={t18} mentionedWaves={t19} referencedNfts={t20} nftLinks={t21} textSize="sm" onQuoteClick={onQuoteClick} currentDropId={t22} embedPath={embedPath} quotePath={effectiveQuotePath} embedDepth={embedDepth} maxEmbedDepth={maxEmbedDepth} /></div>;
    $[43] = effectiveQuotePath;
    $[44] = embedDepth;
    $[45] = embedPath;
    $[46] = maxEmbedDepth;
    $[47] = onQuoteClick;
    $[48] = t17;
    $[49] = t18;
    $[50] = t19;
    $[51] = t20;
    $[52] = t21;
    $[53] = t22;
    $[54] = t23;
  } else {
    t23 = $[54];
  }
  let t24;
  if ($[55] !== t14 || $[56] !== t16 || $[57] !== t23) {
    t24 = <div className="tw-flex tw-w-full tw-flex-col">{t14}{t16}{t23}</div>;
    $[55] = t14;
    $[56] = t16;
    $[57] = t23;
    $[58] = t24;
  } else {
    t24 = $[58];
  }
  let t25;
  if ($[59] !== t24 || $[60] !== t7) {
    t25 = <div className="tw-group tw-relative tw-flex tw-w-full tw-flex-col"><div className="tw-flex tw-gap-x-2">{t7}{t24}</div></div>;
    $[59] = t24;
    $[60] = t7;
    $[61] = t25;
  } else {
    t25 = $[61];
  }
  let t26;
  if ($[62] !== t25 || $[63] !== t5 || $[64] !== t6) {
    t26 = <div className="tw-mt-1 tw-cursor-pointer tw-rounded-xl tw-bg-iron-950 tw-px-3 tw-py-3 tw-ring-1 tw-ring-inset tw-ring-iron-800" onClick={t5} onKeyDown={t6} role="button" tabIndex={0}>{t25}</div>;
    $[62] = t25;
    $[63] = t5;
    $[64] = t6;
    $[65] = t26;
  } else {
    t26 = $[65];
  }
  return t26;
};
export default WaveDropQuote;